import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './watch.css';
import '../../components/editable/editable.css';
import EditableText from '../../components/editable/EditableText';
import VideoEditModal from '../../components/editable/VideoEditModal';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import { resolveWatchSections } from '../../data/watchSections';
import { resolveWatchItem } from '../../data/watchVideos';

const Watch = () => {
  const { isEditing } = useAuth();
  const { getContent, setContentValue } = useContent();
  const sections = resolveWatchSections(getContent);
  const [editingVideo, setEditingVideo] = useState(null);

  const saveSectionVideos = (sectionIndex, videos) => {
    setContentValue(`watch.section.${sectionIndex}.videos`, videos);
  };

  const updateVideoUrl = (sectionIndex, videoIndex, newUrl) => {
    const videos = [...sections[sectionIndex].videos];
    const entry = videos[videoIndex];
    if (typeof entry === 'string') {
      videos[videoIndex] = newUrl;
    } else {
      videos[videoIndex] = { ...entry, url: newUrl };
    }
    saveSectionVideos(sectionIndex, videos);
  };

  const updateVideoThumbnail = (sectionIndex, videoIndex, thumbnail) => {
    const videos = [...sections[sectionIndex].videos];
    const entry = videos[videoIndex];
    if (typeof entry === 'string') {
      videos[videoIndex] = { url: entry, thumbnail };
    } else {
      videos[videoIndex] = { ...entry, thumbnail };
    }
    saveSectionVideos(sectionIndex, videos);
  };

  const removeVideo = (sectionIndex, videoIndex) => {
    saveSectionVideos(
      sectionIndex,
      sections[sectionIndex].videos.filter((_, idx) => idx !== videoIndex)
    );
    setEditingVideo(null);
  };

  const addVideo = (sectionIndex, url) => {
    if (!url?.trim()) return;
    saveSectionVideos(sectionIndex, [...sections[sectionIndex].videos, url.trim()]);
  };

  const activeEdit =
    editingVideo !== null
      ? {
          ...resolveWatchItem(
            sections[editingVideo.sectionIndex].videos[editingVideo.videoIndex],
            sections[editingVideo.sectionIndex]
          ),
          ...editingVideo,
        }
      : null;

  useEffect(() => {
    window.scrollTo(0, 0);

    AOS.init({
      duration: 800,
      offset: 50,
      once: true,
      easing: 'ease-in-out',
    });
    AOS.refresh();

    const handleResize = () => {
      document.body.style.backgroundSize = window.innerWidth < 768 ? 'cover' : 'auto';
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="watch-page">
      <header className="watch-header">
        <EditableText contentKey="watch.hero.title" tag="h1" data-aos="fade-in">
          Watch Streams
        </EditableText>
      </header>

      {sections.map((section, index) => {
        const sectionSlug = section.title.toLowerCase().replace(/\s+/g, '-');
        const isShorts = sectionSlug === 'shorts';

        return (
          <div key={index} className={`watch-section ${sectionSlug}`}>
            <div className="watch-section-content">
              <EditableText
                contentKey={`watch.section.${index}.title`}
                tag="h2"
                className="section-title"
                data-aos="fade-up"
              >
                {section.title}
              </EditableText>
              <div className="divider"></div>
              <div
                className={`videos-grid ${sectionSlug}`}
                data-content-key={`watch.section.${index}.videos`}
              >
                {section.videos.map((entry, idx) => {
                  const { url, thumbnail, alt, isPlaylist } = resolveWatchItem(entry, section);

                  return (
                    <div
                      key={idx}
                      className={`video-item-wrap${isEditing ? ' video-item-wrap--editable' : ''}`}
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`video-item${isShorts ? ' shorts-item' : ''}${isPlaylist ? ' playlist-item' : ''}`}
                        onClick={isEditing ? (e) => e.preventDefault() : undefined}
                      >
                        <img src={thumbnail} alt={alt} />
                        {isPlaylist && <span className="playlist-badge">Playlist</span>}
                        <div className="play-icon"></div>
                      </a>
                      {isEditing && (
                        <button
                          type="button"
                          className="video-edit-trigger"
                          aria-label={`Edit video ${idx + 1}`}
                          onClick={() => setEditingVideo({ sectionIndex: index, videoIndex: idx })}
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  );
                })}
                {isEditing && (
                  <div className={`video-item-wrap video-item-wrap--add${isShorts ? ' video-item-wrap--add-shorts' : ''}`}>
                    <button
                      type="button"
                      className={`watch-video-add${isShorts ? ' watch-video-add--shorts' : ''}`}
                      onClick={() => {
                        const url = window.prompt('YouTube URL for new video');
                        if (url) addVideo(index, url);
                      }}
                    >
                      <span className="watch-video-add-icon">+</span>
                      <span className="watch-video-add-label">Add video</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
            {section.moreLink && (
              <a
                href={section.moreLink}
                target="_blank"
                rel="noopener noreferrer"
                className="view-more-button"
                onClick={isEditing ? (e) => e.preventDefault() : undefined}
              >
                View More
              </a>
            )}
          </div>
        );
      })}

      {activeEdit && (
        <VideoEditModal
          video={activeEdit}
          onClose={() => setEditingVideo(null)}
          onSaveUrl={(newUrl) => {
            updateVideoUrl(activeEdit.sectionIndex, activeEdit.videoIndex, newUrl);
            setEditingVideo(null);
          }}
          onSaveThumbnail={(thumbnail) => {
            updateVideoThumbnail(activeEdit.sectionIndex, activeEdit.videoIndex, thumbnail);
          }}
          onRemove={() => removeVideo(activeEdit.sectionIndex, activeEdit.videoIndex)}
        />
      )}
    </div>
  );
};

export default Watch;
